import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, BehaviorSubject, throwError, of } from 'rxjs';
import { tap, catchError, map, switchMap } from 'rxjs/operators';
import { ConfigService } from './config.service';
import { UserService } from './user.service';
import { environment } from '../../environments/environment';
import { APP_CONSTANTS } from '../constants/app.constants';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface AuthResponse {
  token: string;
  user: User;
}

export interface User {
  id: string;
  email: string;
  name: string;
  displayName: string;
  avatar?: string;
  image?: string;
  spotifyConnected?: boolean;
  discogsConnected?: boolean;
  facebookConnected?: boolean;
  googleConnected?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private tokenKey = 'smartprogressions_token';
  private discogsTokenKey = 'discogs_token';

  constructor(
    private http: HttpClient,
    private router: Router,
    private configService: ConfigService,
    private userService: UserService
  ) {
    this.initializeAuth();
    // Expose service globally for debugging
    (window as any).authService = this;
  }

  private initializeAuth(): void {
    const token = this.getToken();
    if (token) {
      console.log('Token found in localStorage, user will be validated on route access');
    }
  }

  private getApiUrl(): string {
    return this.configService.getApiUrl();
  }

  loginWithEmail(credentials: LoginCredentials): Observable<AuthResponse> {
    this.userService.setLoading(true);
    return this.http.post<AuthResponse>(`${this.getApiUrl()}/auth/login`, credentials).pipe(
      tap(response => {
        this.setToken(response.token);
        this.userService.setUser(response.user);
        this.userService.setLoading(false);
      }),
      catchError(error => {
        this.userService.setLoading(false);
        console.error('Login error:', error);
        return throwError(() => error);
      })
    );
  }

  loginWithOAuth(provider: 'google' | 'facebook' | 'spotify' | 'discogs'): void {
    console.log(`🔐 AuthService: Initiating OAuth login with ${provider}`);

    if (provider === 'discogs') {
      // Para Discogs, usamos el nuevo flujo con popup
      const width = 600;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      window.open(
        `${environment.apiUrl}/auth/discogs`,
        'Discogs Login',
        `width=${width},height=${height},left=${left},top=${top}`
      );
    } else if (provider === 'spotify') {
      // Para Spotify, usamos el flujo de login (no conexión)
      const authUrl = new URL(`${environment.apiUrl}/auth/spotify`);
      authUrl.searchParams.append('type', 'login');
      authUrl.searchParams.append('redirect_uri', `${window.location.origin}${APP_CONSTANTS.ROUTES.LOGIN_CALLBACK}`);

      console.log('🔐 AuthService: Redirecting to Spotify login:', authUrl.toString());
      window.location.href = authUrl.toString();
    } else {
      // Para otros proveedores, mantenemos el flujo actual
      const authUrl = new URL(`${environment.apiUrl}/auth/${provider}`);
      authUrl.searchParams.append('provider', provider);
      authUrl.searchParams.append('redirect_uri', `${window.location.origin}${APP_CONSTANTS.ROUTES.LOGIN_CALLBACK}`);

      console.log(`🔐 AuthService: Redirecting to ${provider} login:`, authUrl.toString());
      window.location.href = authUrl.toString();
    }
  }

  handleOAuthCallback(): Observable<boolean> {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const provider = urlParams.get('provider');

    if (token) {
      if (provider === 'spotify') {
        // Para Spotify, guardamos el token en localStorage
        localStorage.setItem('spotify_token', token);
        return of(true);
      } else {
        // Para otros proveedores, usamos el flujo normal
        this.setToken(token);
        this.userService.setLoading(true);
        return this.getCurrentUser().pipe(
          tap(user => {
            this.userService.setUser(user);
            this.userService.setLoading(false);
          }),
          map(() => true),
          catchError(() => {
            this.userService.setLoading(false);
            this.logout();
            return throwError(() => new Error('Failed to get user info'));
          })
        );
      }
    }

    return throwError(() => new Error('No token found'));
  }

  getCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.getApiUrl()}/auth/me`).pipe(
      catchError(error => {
        console.error('Get current user error:', error);
        return throwError(() => error);
      })
    );
  }

  logout(): void {
    localStorage.removeItem(this.tokenKey);
    this.userService.clearUser();
    this.router.navigate(['/login']);
  }

  getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  private setToken(token: string): void {
    localStorage.setItem(this.tokenKey, token);
  }

  isAuthenticated(): boolean {
    return !!this.getToken();
  }

  getUser(): User | null {
    return this.userService.getUser();
  }

  // Expose user observable for components
  get user$() {
    return this.userService.user$;
  }

  // Method to validate current session with backend
  validateSession(): Observable<boolean> {
    if (!this.getToken()) {
      console.log('validateSession: No token found');
      return of(false);
    }

    console.log('validateSession: Token found, calling getCurrentUser');
    this.userService.setLoading(true);
    return this.getCurrentUser().pipe(
      tap(user => {
        console.log('validateSession: Successfully got user:', user);
        this.userService.setUser(user);
        this.userService.setLoading(false);
      }),
      map(() => true),
      catchError((error) => {
        console.error('validateSession: Error getting user:', error);
        this.userService.setLoading(false);
        this.logout();
        return of(false);
      })
    );
  }

  // Debug method to check auth state
  debugAuthState(): void {
    console.log('=== AUTH DEBUG STATE ===');
    console.log('Token exists:', !!this.getToken());
    console.log('Token value:', this.getToken()?.substring(0, 20) + '...');
    console.log('Is authenticated:', this.isAuthenticated());
    console.log('Current user:', this.getUser());
    console.log('API URL:', this.getApiUrl());
    console.log('========================');
  }

  handleDiscogsAuth(data: any): void {
    if (data.token) {
      localStorage.setItem(this.discogsTokenKey, data.token);
      // Actualizar el estado del usuario
      this.getCurrentUser().subscribe(user => {
        this.userService.setUser(user);
      });
    }
  }

  getDiscogsToken(): string | null {
    return localStorage.getItem(this.discogsTokenKey);
  }
}
